import { Button, Grid } from '@mui/material';
import { useCallback, useContext, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as Sentry from '@sentry/react';
import { useMutation } from '@tanstack/react-query';
import { DATE_FORMATS } from '@callmart/types';
import Container from '../components/Layout/Container';
import UserContext from '../store/User/UserContext';
import {
    useAlertMessage,
    useCookieLocation,
    useCustomStyles,
    useHEMTLocations,
    useLocationsByLoggedInUserRole,
} from '../hooks';
import {
    checkCapacityReportPlausibility,
    createCapacityReport,
    createOneCapacityReport,
    uploadCapacityReports,
} from '../services/capacityReport';
import { createTableRows } from '../utils/agGridUtils';
import SelectExportCapacitiesDateRangeDialog from '../components/CapacityReport/SelectExportCapacitiesDateRangeDialog';
import {
    NUM_OF_MONTH_COLUMNS_FOR_CAPACITY_REPORT,
    plausibilityCheckOptions,
} from '../components/CapacityReport/helpers/constants';
import {
    parseUploadResponse,
    setCommonStateAfterDataUpload,
    showErrorUploadNotification,
    showSuccessfulUploadNotification,
} from '../utils/componentUtils';
import LastUpload from '../components/General/LastUpload';
import LastUploadCapacityReportPopup from '../components/CapacityReport/LastUploadCapacityReportPopup';
import { ReactComponent as FileExport } from '../assets/file-export.svg';
import { createMapWithTableRows, getMonthColumns } from '../components/CapacityReport/helpers/utils';
import CapacityReportTable from '../components/CapacityReport/CapacityReportTable';
import PlausibilityCheckSelect from '../components/Selects/PlausibilityCheckSelect';
import { CapacityReportType } from '../enums/CapacityReportType';
import UserRoleLocation from '../components/General/UserRoleLocation';
import CapacityReportEmptyTemplateDownloadButton from '../components/CapacityReport/CapacityReportEmptyTemplateDownloadButton';
import { parseXLSXData } from '../utils/xlsxUtils';
import UploadXlsxFileButton from '../components/Buttons/UploadXlsxFileButton';
import PercentageLoadingOverlay from '../components/Loaders/PercentageLoadingOverlay';
import CapacityReportCurrentDataDownloadButton from '../components/CapacityReport/CapacityReportCurrentDataDownloadButton';
import GridContainer from '../components/Layout/GridContainer';
import {
    getCapacityReportQueryKey,
    useCapacityReport,
    useLastCapacityReportUploadDate,
    useLastCapacityReportUploadUserAndDate,
    useLatestCapacityReportsByWorktypes,
} from '../hooks/api/capacityReports';
import { queryClient } from '../services/queryClient';
import dayjs from '@utils/dayjs';

const createLastUploadTableRows = (capacityReports) => {
    const rowsMap = new Map();
    for (const report of capacityReports) {
        const key = report.worktype;
        const month = [dayjs(report.date).format(DATE_FORMATS.YEAR_MONTH)];
        const existingEntry = rowsMap.get(key) || {};

        rowsMap.set(key, {
            ...existingEntry,
            [month]: report.sum,
        });
    }

    return createTableRows(rowsMap, 'worktype');
};

const createLastUploadTableColumns = (allMonths) => {
    const columns = [
        {
            field: 'worktype',
            headerName: '',
            minWidth: 120,
        },
    ];

    allMonths.forEach((month) => {
        columns.push({
            field: `${dayjs(month).format(DATE_FORMATS.YEAR_MONTH)}`,
            headerName: `${dayjs(month).format(DATE_FORMATS.MONTH_SHORT)} ${dayjs(month).format(
                DATE_FORMATS.YEAR_TWO_DIGIT
            )}`,
            cellClass: 'grid-cell-centered',
        });
    });

    return columns;
};

function CapacityReport() {
    const { t } = useTranslation('allDeltas');
    const classes = useCustomStyles();

    const { dispatchAlert } = useAlertMessage();
    const loggedInUser = useContext(UserContext);
    // TEF aliases to WFM in HEMTService.
    const canWrite = loggedInUser?.isWFMUser || loggedInUser?.isOSPUser;

    const gridRef = useRef(null);
    const [firstMonth, setFirstMonth] = useState(dayjs());
    const [monthForUploading, setMonthForUploading] = useState(dayjs().format(DATE_FORMATS.YEAR_MONTH));
    const [loading, setLoading] = useState(false);
    const [percentage, setPercentage] = useState(0);
    const [expandedRows, setExpandedRows] = useState([]);
    const [openExportDialog, setOpenExportDialog] = useState(false);

    const allLocationsFromHEMT = useHEMTLocations();
    const [locationOptions] = useLocationsByLoggedInUserRole();
    const [selectedLocation, setSelectedLocation] = useCookieLocation(locationOptions);
    const [selectedPlausibilityCheck, setSelectedPlausibilityCheck] = useState(plausibilityCheckOptions[0].value);

    const [lastUploadPopoverAnchorEl, setLastUploadPopoverAnchorEl] = useState(null);
    const lastCapacityReportUploadDate = useLastCapacityReportUploadDate(selectedLocation);
    const {
        user: lastUploadUser,
        userId: lastUploadUserId,
        date: lastUploadUserDate,
    } = useLastCapacityReportUploadUserAndDate(selectedLocation);
    const lastUploadData = useLatestCapacityReportsByWorktypes({
        location: selectedLocation,
        user: lastUploadUserId,
        showLastUploadInfo: lastUploadPopoverAnchorEl,
    });
    const lastUploadTableRows = lastUploadData.capacityReport
        ? createLastUploadTableRows(lastUploadData.capacityReport)
        : [];
    const lastUploadColumnDefs = lastUploadData.allMonths ? createLastUploadTableColumns(lastUploadData.allMonths) : [];
    const lastUploadDate = lastUploadUserDate || lastCapacityReportUploadDate;

    const capacityReportQueryKey = getCapacityReportQueryKey({
        date: firstMonth,
        location: selectedLocation,
        plausibilityCheck: selectedPlausibilityCheck,
        numOfMonthColumns: NUM_OF_MONTH_COLUMNS_FOR_CAPACITY_REPORT,
    });

    const monthColumns = useMemo(() => getMonthColumns(firstMonth), [firstMonth]);

    const { data: capacityReports } = useCapacityReport({
        date: firstMonth,
        location: selectedLocation,
        plausibilityCheck: selectedPlausibilityCheck,
        numOfMonthColumns: NUM_OF_MONTH_COLUMNS_FOR_CAPACITY_REPORT,
    });
    const tableRows = useMemo(
        () =>
            capacityReports.length > 0
                ? createTableRows(createMapWithTableRows(capacityReports, monthColumns, 'capacityReports'), 'id')
                : [],
        [capacityReports, monthColumns]
    );

    const getExpandedRowsInTable = useCallback(() => {
        const expandedRows = [];
        gridRef.current?.api?.forEachNode((node) => {
            if (node.displayed && node.expanded) {
                expandedRows.push(node);
            }
        });

        return expandedRows;
    }, []);

    const { mutateAsync: handlePlausibilityCheck } = useMutation({
        mutationFn: checkCapacityReportPlausibility,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: capacityReportQueryKey });
        },
        onError: (error) => {
            Sentry.captureException(error);
            dispatchAlert({
                message: t('notification.checkFailed'),
                type: 'error',
            });
        },
    });

    const { mutateAsync: handleCreateCapacityReport } = useMutation({
        mutationFn: ({ data, userId }) => createCapacityReport(data, userId),
        onSuccess: () => {
            // Refetch after bulk save to ensure sync with server
            queryClient.invalidateQueries({ queryKey: capacityReportQueryKey });
        },
    });
    const { mutateAsync: handleCreateOneCapacityReport } = useMutation({
        mutationFn: createOneCapacityReport,
        onSuccess: () => {
            // Refetch after save to sync with server (ag-Grid preserves cell via getRowId)
            queryClient.invalidateQueries({ queryKey: capacityReportQueryKey });
        },
    });
    const { mutateAsync: handleUploadCapacityReports } = useMutation({
        mutationFn: uploadCapacityReports,
    });

    const saveCapacityReport = useCallback(
        async ({ tableRows, loggedInUserId, cellParams }) => {
            try {
                let numOfNewReports = 0;
                if (cellParams) {
                    // Single-cell edit: ag-Grid keeps the value by default, no state changes needed
                    const capacityReportValue = cellParams.newValue;
                    const data = {
                        changedCapacity: {
                            capacityId: parseInt(cellParams.data.id),
                            capacityTypeName: cellParams.data.capacityType.name,
                            date: cellParams.column.colId,
                            value: capacityReportValue,
                            capacityType: cellParams.data.capacityType,
                        },
                        userId: loggedInUserId,
                    };

                    await handleCreateOneCapacityReport(data);
                    numOfNewReports = 1;
                } else {
                    // Bulk save: preserve expanded rows before data refresh
                    setExpandedRows(getExpandedRowsInTable());
                    const result = await handleCreateCapacityReport({ data: tableRows, userId: loggedInUserId });
                    numOfNewReports = result.numOfNewReports;
                }

                if (numOfNewReports === 0) {
                    dispatchAlert({
                        message: t('notification.invalidData'),
                        type: 'error',
                    });
                } else {
                    dispatchAlert({
                        message: t('notification.reportCreated'),
                        type: 'success',
                    });
                }
            } catch (error) {
                Sentry.captureException(error);
                dispatchAlert({
                    message: t('notification.saveFailed'),
                    type: 'error',
                });
            }
        },
        [dispatchAlert, getExpandedRowsInTable, handleCreateCapacityReport, handleCreateOneCapacityReport, t]
    );

    const handleExportDialogClose = () => {
        setOpenExportDialog(false);
    };

    const handleExportDialogOpen = () => {
        setOpenExportDialog(true);
    };

    const uploadFiles = (files, closeDropzoneDialog) => {
        setLoading(true);

        const file = files[0];

        const reader = new FileReader();
        reader.onload = async (e) => {
            const data = parseXLSXData(e);

            handleUploadCapacityReports({ data, user: loggedInUser?.id, setPercentage })
                .then(async (res) => {
                    const resData = parseUploadResponse(res, 'Capacity report');
                    const { success, errorMessage } = resData;

                    if (success) {
                        showSuccessfulUploadNotification(dispatchAlert);
                        queryClient.invalidateQueries(capacityReportQueryKey);
                    } else {
                        showErrorUploadNotification({
                            dispatchAlert,
                            errorMessage,
                        });
                    }
                })
                .catch(async (error) => {
                    Sentry.captureException(error);
                    showErrorUploadNotification({
                        dispatchAlert,
                        errorMessage: error.message,
                    });
                })
                .finally(() => {
                    setCommonStateAfterDataUpload(setLoading, setPercentage, closeDropzoneDialog);
                });
        };
        reader.readAsArrayBuffer(file);
    };

    const showLastUploadData = (event) => {
        setLastUploadPopoverAnchorEl(event.currentTarget);
    };

    const hideLastUploadData = () => {
        setLastUploadPopoverAnchorEl(null);
    };

    return (
        <Container>
            <GridContainer>
                {loggedInUser && (
                    <UserRoleLocation
                        loggedInUser={loggedInUser}
                        selectedLocation={selectedLocation}
                        locationOptions={locationOptions}
                        onChange={setSelectedLocation}
                    ></UserRoleLocation>
                )}
                <Grid item xs={12} md={3} lg={2}>
                    <PlausibilityCheckSelect
                        selectedValue={selectedPlausibilityCheck}
                        onChange={setSelectedPlausibilityCheck}
                    ></PlausibilityCheckSelect>
                </Grid>
                <Grid item xs={10} md={4} lg={3}>
                    <LastUpload
                        showInfoIcon={true}
                        onClick={(e) => showLastUploadData(e)}
                        date={lastUploadDate}
                        setLastUploadPopoverAnchorEl={setLastUploadPopoverAnchorEl}
                    ></LastUpload>
                </Grid>
                <Grid item xs={2} md={1} lg={4} sx={classes.btnRightAligned}>
                    <Button onClick={handleExportDialogOpen} variant="contained">
                        <FileExport />
                    </Button>
                </Grid>
                <Grid item xs={12}>
                    {canWrite && (
                        <Button
                            onClick={() => handlePlausibilityCheck({ data: tableRows, locationId: selectedLocation })}
                            variant="outlined"
                            style={{ marginRight: '5px' }}
                        >
                            {t('plausibility.check')}
                        </Button>
                    )}
                    <CapacityReportEmptyTemplateDownloadButton
                        months={getMonthColumns(firstMonth)}
                        location={selectedLocation}
                    ></CapacityReportEmptyTemplateDownloadButton>
                    <CapacityReportCurrentDataDownloadButton
                        startDate={firstMonth}
                        selectedLocation={selectedLocation}
                        selectedPlausibilityCheck={selectedPlausibilityCheck}
                        allLocationsFromHEMT={allLocationsFromHEMT}
                    ></CapacityReportCurrentDataDownloadButton>
                    {canWrite && (
                        <UploadXlsxFileButton
                            buttonName={t('action.upload')}
                            monthForUploading={monthForUploading}
                            onChangeMonthForUploading={setMonthForUploading}
                            uploadFiles={uploadFiles}
                            dialogTitle={t('upload.capacityDialogTitle')}
                        ></UploadXlsxFileButton>
                    )}
                </Grid>
                <Grid item xs={12}>
                    <CapacityReportTable
                        ref={gridRef}
                        firstMonth={firstMonth}
                        onFirstMonthChanged={setFirstMonth}
                        tableRows={tableRows}
                        expandedRows={expandedRows}
                        onExpandedRowsChanged={setExpandedRows}
                        getExpandedRowsInTable={getExpandedRowsInTable}
                        canWrite={canWrite}
                        saveCapacityReport={(params) =>
                            saveCapacityReport({
                                tableRows,
                                loggedInUserId: loggedInUser?.id,
                                firstMonth,
                                selectedLocation,
                                ...params,
                            })
                        }
                    ></CapacityReportTable>
                </Grid>
                <Grid item sm={6} md={8} lg={9}></Grid>
                {canWrite && (
                    <Grid item xs={12} sm={6} md={4} lg={3} sx={classes.btnRightAligned}>
                        <Button
                            variant="outlined"
                            onClick={() =>
                                saveCapacityReport({
                                    tableRows,
                                    loggedInUserId: loggedInUser?.id,
                                    firstMonth,
                                    selectedLocation,
                                })
                            }
                        >
                            {t('action.save')}
                        </Button>
                    </Grid>
                )}

                {openExportDialog && (
                    <SelectExportCapacitiesDateRangeDialog
                        open={openExportDialog}
                        onClose={handleExportDialogClose}
                        locations={locationOptions}
                        reportType={CapacityReportType.CAPACITY_REPORT}
                    ></SelectExportCapacitiesDateRangeDialog>
                )}
                {lastUploadPopoverAnchorEl && (
                    <LastUploadCapacityReportPopup
                        anchorEl={lastUploadPopoverAnchorEl}
                        onPopoverClose={hideLastUploadData}
                        location={selectedLocation}
                        lastUploadDate={lastUploadUserDate}
                        lastUploadUser={lastUploadUser}
                        tableRows={lastUploadTableRows}
                        columnDefs={lastUploadColumnDefs}
                    ></LastUploadCapacityReportPopup>
                )}
                <PercentageLoadingOverlay loading={loading} percentage={percentage}></PercentageLoadingOverlay>
            </GridContainer>
        </Container>
    );
}

export default CapacityReport;
